/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';

import {
    User,
    CreditCard,
    ShieldCheck,
    ChevronRight,
    Camera,
    Mail,
    Calendar,
    Sparkles,
    Pencil,
    LayoutDashboard,
    Wallet,
    Bell,
    Settings,
    FileText,
    Check,
    Globe,
    X,
    ArrowUp,
    ArrowDown,
    Trash2,
    Plus,
    Save,
    Heart,
    ChevronDown,
    LogOut,
    Search,
    Compass
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import CVSelectionModal from '../CVConfiguration/components/CVSelectionModal';

import { INITIAL_COUNTRIES, SECTORS_LIST } from '../../constants/sharedData';

const LANGUAGES_LIST = [
    "Allemand", "Anglais", "Arabe", "Chinois", "Coréen", "Danois", "Espagnol",
    "Finlandais", "Français", "Grec", "Hindi", "Hébreu", "Indonésien", "Italien",
    "Suédois", "Thaï", "Turc", "Vietnamien"
];

import { getAvatarFaceUrl } from '../../utils/getAvatarFaceUrl';

const ModernSelect = ({ value, options, onChange, placeholder, className = "" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Reset search when closed
    useEffect(() => {
        if (!isOpen) setSearchTerm("");
    }, [isOpen]);

    const selectedOption = options.find(opt => opt.value === value || opt === value);
    const displayLabel = selectedOption?.label || selectedOption || placeholder;

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <button
                type="button"
                onClick={() => {
                    setIsOpen(!isOpen);
                    if (!isOpen) setTimeout(() => searchInputRef.current?.focus(), 100);
                }}
                className="w-full flex items-center justify-between gap-2 p-2.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:border-blue-300 focus:ring-4 focus:ring-blue-100 transition-all active:scale-[0.98] shadow-sm"
            >
                <span className="truncate">{displayLabel}</span>
                <ChevronDown size={14} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-100 rounded-2xl shadow-2xl shadow-slate-300/30 z-50 overflow-hidden"
                    >
                        <div className="p-2 border-b border-slate-100 sticky top-0 bg-white z-10">
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="w-full p-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-300"
                                placeholder="Rechercher..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                        <div className="max-h-60 overflow-y-auto py-1">
                            {options.filter(opt => {
                                const label = opt.label || opt;
                                return String(label).toLowerCase().includes(searchTerm.toLowerCase());
                            }).map((opt, i) => {
                                const val = opt.value || opt;
                                const label = opt.label || opt;
                                const isSelected = val === value;
                                return (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => {
                                            onChange(val);
                                            setIsOpen(false);
                                        }}
                                        className={`w-full text-left px-4 py-2.5 text-xs font-bold transition-all border-b border-slate-50 last:border-0
                                            ${isSelected
                                                ? 'bg-blue-50 text-blue-600'
                                                : 'text-slate-600 hover:bg-slate-50 hover:text-blue-600'
                                            }`}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                            {options.filter(opt => {
                                const label = opt.label || opt;
                                return String(label).toLowerCase().includes(searchTerm.toLowerCase());
                            }).length === 0 && (
                                    <div className="px-4 py-3 text-xs text-slate-400 text-center italic">
                                        Aucun résultat
                                    </div>
                                )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};



const SpecializationTags = ({ specs }) => (
    <div className="flex flex-wrap gap-2">
        {specs && specs.length > 0 ? (
            specs.map((sect, i) => (
                <span key={i} className="bg-blue-100 text-blue-700 px-2 py-1 rounded-md text-xs font-bold border border-blue-200">
                    {sect}
                </span>
            ))
        ) : (
            <span className="text-slate-400 italic text-xs">Non renseigné</span>
        )}
    </div>
);

const SpecializationEditor = ({ specs = [], onUpdate }) => {
    const [selectedToAdd, setSelectedToAdd] = useState("");

    const handleAdd = () => {
        if (selectedToAdd && !specs.includes(selectedToAdd)) {
            onUpdate([...specs, selectedToAdd]);
            setSelectedToAdd("");
        }
    };

    const handleRemove = (index) => {
        const updated = [...specs];
        updated.splice(index, 1);
        onUpdate(updated);
    };

    const handleMove = (index, direction) => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === specs.length - 1) return;

        const updated = [...specs];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        const temp = updated[targetIndex];
        updated[targetIndex] = updated[index];
        updated[index] = temp;
        onUpdate(updated);
    };

    return (
        <div className="w-full">
            <div className="mb-4 flex gap-2">
                <ModernSelect
                    value={selectedToAdd}
                    options={SECTORS_LIST.filter(s => !specs.includes(s))}
                    onChange={setSelectedToAdd}
                    placeholder="Sélectionner un secteur..."
                    className="flex-1"
                />
                <button
                    onClick={handleAdd}
                    disabled={!selectedToAdd}
                    className="bg-blue-600 text-white p-2 rounded-xl disabled:opacity-50 hover:bg-blue-700 transition-colors"
                >
                    <Plus size={20} />
                </button>
            </div>

            <div className="space-y-2">
                {specs.map((spec, idx) => (
                    <div key={`${spec}-${idx}`} className="flex items-center justify-between p-2 rounded-xl border bg-white border-slate-200">
                        <div className="flex items-center gap-3">
                            <div className="flex flex-col items-center gap-1">
                                <button
                                    onClick={() => handleMove(idx, 'up')}
                                    disabled={idx === 0}
                                    className="text-slate-400 hover:text-blue-600 disabled:opacity-30"
                                >
                                    <ArrowUp size={12} />
                                </button>
                                <span className="text-[10px] font-bold text-slate-300">{idx + 1}</span>
                                <button
                                    onClick={() => handleMove(idx, 'down')}
                                    disabled={idx === specs.length - 1}
                                    className="text-slate-400 hover:text-blue-600 disabled:opacity-30"
                                >
                                    <ArrowDown size={12} />
                                </button>
                            </div>
                            <span className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-bold shadow-sm">
                                {spec}
                            </span>
                        </div>

                        <button
                            onClick={() => handleRemove(idx)}
                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

const SpecializationSection = ({ specs, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [localSpecs, setLocalSpecs] = useState(specs || []);

    useEffect(() => {
        setLocalSpecs(specs || []);
    }, [specs]);

    const handleSave = () => {
        onUpdate(localSpecs);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setLocalSpecs(specs || []);
        setIsEditing(false);
    };

    return (
        <div className="mt-12 pt-12 border-t border-slate-100">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-black text-slate-900">Spécialisations (Secteurs)</h3>
                {!isEditing ? (
                    <div className="flex gap-2">
                        <button
                            onClick={() => setIsEditing(true)}
                            className="flex items-center gap-2 px-3 h-8 bg-white border border-slate-200 text-slate-600 rounded-lg text-xs font-bold hover:border-blue-200 hover:text-blue-600 transition-colors"
                        >
                            <Pencil size={14} />
                            Modifier
                        </button>
                    </div>
                ) : (
                    <div className="flex gap-2">
                        <button onClick={handleSave} className="flex items-center gap-2 px-3 h-8 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition-colors shadow-md">
                            <Save size={14} />
                            Enregistrer
                        </button>
                        <button onClick={handleCancel} className="flex items-center gap-2 px-3 h-8 bg-white border border-slate-200 text-slate-500 rounded-lg text-xs font-bold hover:bg-slate-50 transition-colors">
                            <X size={14} />
                        </button>
                    </div>
                )}

            </div>

            <div className={`bg-slate-50 p-6 rounded-3xl border border-slate-100 transition-colors`}>
                {isEditing ? (
                    <SpecializationEditor specs={localSpecs} onUpdate={setLocalSpecs} />
                ) : (
                    <SpecializationTags specs={specs} />
                )}
            </div>
        </div>
    );
};

// --- NEW COUNTRIES SECTION ---
const CountryEditor = ({ countries = [], allAvailableCountries = [], onUpdate }) => {
    const [search, setSearch] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsDropdownOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter out already selected, then filter by search
    const availableOptions = allAvailableCountries
        .filter(c => !countries.includes(c))
        .filter(c => c.toLowerCase().includes(search.toLowerCase()));

    const handleAdd = (countryName) => {
        if (!countries.includes(countryName)) {
            onUpdate([...countries, countryName]);
        }
        setSearch('');
        setIsDropdownOpen(false);
    };

    const handleRemove = (code) => {
        onUpdate(countries.filter(c => c !== code));
    };

    return (
        <div className="w-full">
            <div className="mb-4 relative" ref={dropdownRef}>
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            ref={inputRef}
                            type="text"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setIsDropdownOpen(true); }}
                            onFocus={() => setIsDropdownOpen(true)}
                            placeholder="Rechercher un pays..."
                            className="w-full pl-9 pr-3 py-3 text-sm font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:border-blue-300 focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                        />
                    </div>
                </div>
                {isDropdownOpen && availableOptions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-100 rounded-2xl shadow-2xl shadow-slate-300/30 z-50 overflow-hidden max-h-60 overflow-y-auto">
                        {availableOptions.map((countryName, i) => {
                            const known = INITIAL_COUNTRIES.find(ic => ic.name.toUpperCase() === countryName.toUpperCase());
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => handleAdd(countryName)}
                                    className="w-full text-left px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-blue-50 hover:text-blue-600 transition-all border-b border-slate-50 last:border-0 flex items-center gap-2"
                                >
                                    <span className="text-base">{known?.flag || '🌍'}</span>
                                    <span>{known?.name || countryName}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="flex flex-wrap gap-2">
                {countries.map((countryName) => {
                    const known = INITIAL_COUNTRIES.find(ic => ic.name.toUpperCase() === countryName.toUpperCase()) || {};
                    const label = known.name ? `${known.flag} ${known.name}` : countryName;

                    return (
                        <div key={countryName} className="flex items-center gap-2 pl-3 pr-2 py-2 bg-white border border-slate-200 rounded-xl">
                            <span className="text-sm font-bold text-slate-700">{label}</span>
                            <button
                                onClick={() => handleRemove(countryName)}
                                className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                                <Trash2 size={12} />
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const CountriesSection = ({ countries, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [localCountries, setLocalCountries] = useState(countries || []);
    const [allAvailableCountries, setAllAvailableCountries] = useState([]);

    // Fetch all countries via efficient RPC
    useEffect(() => {
        const fetchAllCountries = async () => {
            try {
                const { data, error } = await supabase.rpc('get_all_distinct_countries');
                if (error) throw error;
                if (data) {
                    const unique = data.filter(c => c && c.trim()).sort();
                    setAllAvailableCountries(unique);
                }
            } catch (err) {
                console.error('Error fetching countries:', err);
            }
        };
        fetchAllCountries();
    }, []);

    useEffect(() => {
        setLocalCountries(countries || []);
    }, [countries]);

    const handleSave = () => {
        onUpdate(localCountries);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setLocalCountries(countries || []);
        setIsEditing(false);
    };

    return (
        <div className="mt-12 pt-12 border-t border-slate-100">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-black text-slate-900">Pays Préférés (Cible)</h3>
                {!isEditing ? (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-2 px-3 h-8 bg-white border border-slate-200 text-slate-600 rounded-lg text-xs font-bold hover:border-blue-200 hover:text-blue-600 transition-colors"
                    >
                        <Pencil size={14} />
                        Modifier
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button onClick={handleSave} className="flex items-center gap-2 px-3 h-8 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition-colors shadow-md">
                            <Save size={14} />
                            Enregistrer
                        </button>
                        <button onClick={handleCancel} className="flex items-center gap-2 px-3 h-8 bg-white border border-slate-200 text-slate-500 rounded-lg text-xs font-bold hover:bg-slate-50 transition-colors">
                            <X size={14} />
                        </button>
                    </div>
                )}
            </div>

            <div className={`bg-slate-50 p-6 rounded-3xl border border-slate-100 transition-colors`}>
                {isEditing ? (
                    <CountryEditor
                        countries={localCountries}
                        allAvailableCountries={allAvailableCountries}
                        onUpdate={setLocalCountries}
                    />
                ) : (
                    <div className="flex flex-wrap gap-2">
                        {countries && countries.length > 0 ? (
                            countries.map(countryName => {
                                const known = INITIAL_COUNTRIES.find(c => c.name.toUpperCase() === countryName.toUpperCase()) || {};
                                const label = known.name ? `${known.flag} ${known.name}` : countryName;
                                return (
                                    <span key={countryName} className="bg-white text-slate-700 px-3 py-2 rounded-xl text-xs font-bold border border-slate-200 shadow-sm flex items-center gap-2">
                                        <span className="text-base">{known.flag || '🌍'}</span>
                                        <span>{known.name || countryName}</span>
                                    </span>
                                );
                            })
                        ) : (
                            <span className="text-slate-400 italic text-xs">Aucun pays sélectionné</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};


const LanguagesCard = ({ languages, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [localLangs, setLocalLangs] = useState(languages || []);
    const [newLang, setNewLang] = useState({ language: '', level: 'Intermédiaire' });

    useEffect(() => {
        setLocalLangs(languages || []);
    }, [languages]);

    const handleAdd = () => {
        if (newLang.language && !localLangs.some(l => l.language === newLang.language)) {
            setLocalLangs([...localLangs, newLang]);
            setNewLang({ language: '', level: 'Intermédiaire' });
        }
    };

    const handleRemove = (index) => {
        const updated = [...localLangs];
        updated.splice(index, 1);
        setLocalLangs(updated);
    };

    const handleLevelChange = (index, newLevel) => {
        const updated = [...localLangs];
        updated[index] = { ...updated[index], level: newLevel };
        setLocalLangs(updated);
    };

    const handleSave = () => {
        onUpdate(localLangs);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setLocalLangs(languages || []);
        setIsEditing(false);
    };

    return (
        <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <h4 className="font-bold text-slate-700 text-sm uppercase tracking-wider">Langues</h4>
                {!isEditing ? (
                    <button onClick={() => setIsEditing(true)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-white rounded-lg transition-all shadow-sm">
                        <Pencil size={14} />
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button onClick={handleSave} className="p-1.5 bg-blue-600 text-white hover:bg-blue-700 rounded-lg shadow-md transition-all">
                            <Check size={14} />
                        </button>
                        <button onClick={handleCancel} className="p-1.5 bg-white text-slate-400 border border-slate-200 hover:bg-slate-50 rounded-lg shadow-sm transition-all">
                            <X size={14} />
                        </button>
                    </div>
                )}
            </div>

            <div className="space-y-3 flex-1">
                {localLangs.map((lang, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-white p-3 rounded-2xl border border-blue-50/50 shadow-sm group">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-xs">
                                {lang.language.substring(0, 2).toUpperCase()}
                            </div>
                            <span className="font-bold text-slate-800">{lang.language}</span>
                        </div>

                        <div className="flex items-center gap-2">
                            {isEditing ? (
                                <ModernSelect
                                    value={lang.level}
                                    options={["Débutant", "Intermédiaire", "Courant", "Bilingue", "Maternel"]}
                                    onChange={(val) => handleLevelChange(idx, val)}
                                    className="w-28"
                                />
                            ) : (
                                <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full uppercase tracking-wider border border-blue-100/50">{lang.level}</span>
                            )}
                            {isEditing && (
                                <button onClick={() => handleRemove(idx)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all">
                                    <Trash2 size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                ))}

                {isEditing && (
                    <div className="mt-6 pt-6 border-t border-slate-200/60">
                        <div className="flex flex-wrap items-center gap-2">
                            <ModernSelect
                                value={newLang.language}
                                options={LANGUAGES_LIST.filter(l => !localLangs.some(ll => ll.language === l))}
                                onChange={(val) => setNewLang({ ...newLang, language: val })}
                                placeholder="Choisir une langue"
                                className="flex-1 min-w-[150px]"
                            />
                            <ModernSelect
                                value={newLang.level}
                                options={["Débutant", "Intermédiaire", "Courant", "Bilingue", "Maternel"]}
                                onChange={(val) => setNewLang({ ...newLang, level: val })}
                                className="w-28"
                            />
                            <button
                                onClick={handleAdd}
                                disabled={!newLang.language}
                                className="w-10 h-10 flex items-center justify-center bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:bg-slate-300 shadow-lg shadow-blue-200 transition-all transform active:scale-95"
                            >
                                <Plus size={18} />
                            </button>
                        </div>
                    </div>
                )}

                {localLangs.length === 0 && !isEditing && (
                    <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                        <Globe size={24} className="mb-2 opacity-20" />
                        <p className="text-xs font-medium italic">Aucune langue renseignée</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const SkillsCard = ({ skills, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [localSkills, setLocalSkills] = useState(skills || []);
    const [newSkill, setNewSkill] = useState("");

    useEffect(() => {
        setLocalSkills(skills || []);
    }, [skills]);

    const handleAdd = () => {
        if (newSkill && !localSkills.includes(newSkill)) {
            setLocalSkills([...localSkills, newSkill]);
            setNewSkill("");
        }
    };

    const handleRemove = (index) => {
        const updated = [...localSkills];
        updated.splice(index, 1);
        setLocalSkills(updated);
    };

    const handleSave = () => {
        onUpdate(localSkills);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setLocalSkills(skills || []);
        setIsEditing(false);
    };

    return (
        <div className={`bg-slate-50 p-6 rounded-3xl border border-slate-100 h-full transition-colors`}>
            <div className="flex justify-between items-center mb-4">
                <h4 className="font-bold text-slate-700 text-sm uppercase tracking-wider">Compétences</h4>
                {!isEditing ? (
                    <div className="flex gap-1">
                        <button onClick={() => setIsEditing(true)} className="text-slate-400 hover:text-blue-600 transition-colors">
                            <Pencil size={14} />
                        </button>
                    </div>
                ) : (
                    <div className="flex gap-1">
                        <button onClick={handleSave} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Check size={14} /></button>
                        <button onClick={handleCancel} className="p-1 text-slate-400 hover:bg-slate-100 rounded"><X size={14} /></button>
                    </div>
                )}
            </div>

            <div className="flex flex-wrap gap-2">
                {localSkills.map((skill, idx) => (
                    <span key={idx} className="bg-white text-slate-700 px-3 py-2 rounded-xl text-xs font-bold border border-slate-200 shadow-sm flex items-center gap-2">
                        {skill}
                        {isEditing && (
                            <button onClick={() => handleRemove(idx)} className="text-slate-400 hover:text-red-500">
                                <X size={12} />
                            </button>
                        )}
                    </span>
                ))}

                {isEditing && (
                    <div className="flex gap-1 items-center bg-white px-2 py-1 rounded-xl border border-blue-200">
                        <input
                            type="text"
                            value={newSkill}
                            onChange={(e) => setNewSkill(e.target.value)}
                            className="w-24 text-xs outline-none"
                            placeholder="Ajouter..."
                            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                        />
                        <button onClick={handleAdd} className="text-blue-600 hover:text-blue-800">
                            <Plus size={14} />
                        </button>
                    </div>
                )}

                {localSkills.length === 0 && !isEditing && <p className="text-xs text-slate-400">Aucune compétence renseignée</p>}
            </div>
        </div>
    );
};

const GeneratedBadge = () => (
    <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg text-xs font-bold mb-4">
        <Sparkles size={12} />
        <span>Généré à partir du CV</span>
    </div>
);

const ExperienceCard = ({ exp, onUpdate, onValidate, onRemove, isNew = false }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isEditing, setIsEditing] = useState(isNew);
    const [editedExp, setEditedExp] = useState(exp);

    // Sync state if prop changes (e.g. new CV upload)
    useEffect(() => {
        setEditedExp(exp);
    }, [exp]);

    const handleSave = () => {
        onUpdate(editedExp);
        setIsEditing(false);
    };

    const handleCancel = () => {
        if (isNew) {
            onRemove(); // If it's a new, unsaved card, remove it on cancel
        } else {
            setEditedExp(exp); // Revert
            setIsEditing(false);
        }
    };

    const handleChange = (field, value) => {
        setEditedExp(prev => ({ ...prev, [field]: value }));
    };

    return (
        <div className={`bg-white rounded-2xl p-6 border-2 border-dashed ${isEditing ? 'border-blue-400 shadow-lg' : 'border-blue-100 hover:border-blue-200'} mb-6 relative group transition-all duration-300`}>
            {/* Header Actions */}
            <div className="flex items-start justify-between mb-6">
                {exp.fromCV ? <GeneratedBadge /> : <div />}
                <div className="flex items-center gap-2">
                    {isEditing ? (
                        <>
                            <button onClick={handleSave} className="flex items-center gap-2 px-3 h-8 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition-colors shadow-md">
                                <Check size={14} />
                                Sauvegarder
                            </button>
                            <button onClick={handleCancel} className="flex items-center gap-2 px-3 h-8 bg-white border border-slate-200 text-slate-500 rounded-lg text-xs font-bold hover:bg-slate-50 transition-colors">
                                <X size={14} />
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={onRemove}
                                className="w-8 h-8 flex items-center justify-center bg-white border border-slate-200 text-slate-400 rounded-lg hover:text-red-500 hover:border-red-200 transition-colors"
                                title="Supprimer"
                            >
                                <X size={16} />
                            </button>
                            <button
                                onClick={() => setIsEditing(true)}
                                className="flex items-center gap-2 px-3 h-8 bg-white border border-slate-200 text-slate-600 rounded-lg text-xs font-bold hover:border-blue-200 hover:text-blue-600 transition-colors"
                            >
                                <Pencil size={14} />
                                Modifier
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Grid Content */}
            <div className="flex flex-col gap-4 md:grid md:grid-cols-[140px_1fr] md:gap-y-4 md:gap-x-4 md:items-baseline text-sm">

                {/* Intitulé */}
                <span className="text-slate-400 font-medium">Intitulé de poste</span>
                {isEditing ? (
                    <input
                        type="text"
                        value={editedExp.title}
                        onChange={(e) => handleChange('title', e.target.value)}
                        className="w-full p-2 border border-slate-200 rounded-lg font-bold text-slate-900 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                ) : (
                    <h4 className="font-black text-slate-900 text-base uppercase tracking-wide">{editedExp.title}</h4>
                )}

                {/* Entreprise */}
                <span className="text-slate-400 font-medium">Entreprise</span>
                {isEditing ? (
                    <input
                        type="text"
                        value={editedExp.company}
                        onChange={(e) => handleChange('company', e.target.value)}
                        className="w-full p-2 border border-slate-200 rounded-lg font-bold text-slate-800 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                ) : (
                    <div className="font-bold text-slate-800">
                        {editedExp.company}
                    </div>
                )}

                {/* Localisation */}
                <span className="text-slate-400 font-medium">Localisation</span>
                {isEditing ? (
                    <input
                        type="text"
                        value={editedExp.location}
                        onChange={(e) => handleChange('location', e.target.value)}
                        className="w-full p-2 border border-slate-200 rounded-lg text-slate-600 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                ) : (
                    <span className="text-slate-600 font-medium">{editedExp.location || 'Non renseigné'}</span>
                )}

                {/* Contrat */}
                <span className="text-slate-400 font-medium">Type de contrat</span>
                {isEditing ? (
                    <input
                        type="text"
                        value={editedExp.contractType}
                        onChange={(e) => handleChange('contractType', e.target.value)}
                        className="w-full p-2 border border-slate-200 rounded-lg text-slate-600 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                ) : (
                    <span className="text-slate-600 font-medium">{editedExp.contractType || 'Non renseigné'}</span>
                )}

                {/* Date */}
                <span className="text-slate-400 font-medium">Date</span>
                {isEditing ? (
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={editedExp.startDate}
                            placeholder="Début"
                            onChange={(e) => handleChange('startDate', e.target.value)}
                            className="w-32 p-2 border border-slate-200 rounded-lg text-slate-600 focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                        <span className="text-slate-400 font-bold">à</span>
                        <input
                            type="text"
                            value={editedExp.endDate}
                            placeholder="Fin"
                            onChange={(e) => handleChange('endDate', e.target.value)}
                            className="w-32 p-2 border border-slate-200 rounded-lg text-slate-600 focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                    </div>
                ) : (
                    <span className="text-slate-600 font-medium">{editedExp.startDate} à {editedExp.endDate}</span>
                )}

                {/* Spécialisation (Secteur) - Added Request */}
                <span className="text-slate-400 font-medium">Spécialisation</span>
                <div className="w-full">
                    {isEditing ? (
                        <SpecializationEditor
                            specs={editedExp.sector || []}
                            onUpdate={(newSects) => handleChange('sector', newSects)}
                        />
                    ) : (
                        <SpecializationTags specs={editedExp.sector} />
                    )}
                </div>

                {/* Description */}
                <span className="text-slate-400 font-medium self-start mt-1">Description</span>
                {isEditing ? (
                    <textarea
                        value={editedExp.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        rows={4}
                        className="w-full p-2 border border-slate-200 rounded-lg text-slate-600 leading-relaxed focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                    />
                ) : (
                    <div>
                        <p className={`text-slate-600 leading-relaxed ${!isExpanded && 'line-clamp-3'}`}>
                            {editedExp.description}
                        </p>
                        {editedExp.description && editedExp.description.length > 150 && (
                            <button
                                onClick={() => setIsExpanded(!isExpanded)}
                                className="text-blue-600 text-xs font-bold mt-2 hover:underline flex items-center gap-1"
                            >
                                {isExpanded ? 'Voir moins' : 'Voir plus'}
                                <ChevronRight size={12} className={`transform transition-transform ${isExpanded ? '-rotate-90' : 'rotate-90'}`} />
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const FormationCard = ({ edu, onUpdate, onValidate, onRemove, isNew = false }) => {
    const [isEditing, setIsEditing] = useState(isNew);
    const [editedEdu, setEditedEdu] = useState(edu);
    const [newSkill, setNewSkill] = useState("");

    const handleAddSkill = () => {
        if (newSkill && !(editedEdu.skills || []).includes(newSkill)) {
            setEditedEdu(prev => ({
                ...prev,
                skills: [...(prev.skills || []), newSkill]
            }));
            setNewSkill("");
        }
    };

    const handleRemoveSkill = (skillToRemove) => {
        setEditedEdu(prev => ({
            ...prev,
            skills: (prev.skills || []).filter(s => s !== skillToRemove)
        }));
    };

    useEffect(() => {
        setEditedEdu(edu);
    }, [edu]);

    const handleSave = () => {
        onUpdate(editedEdu);
        setIsEditing(false);
    };
    const handleCancel = () => {
        if (isNew) {
            onRemove();
        } else {
            setEditedEdu(edu);
            setIsEditing(false);
        }
    };

    const handleChange = (field, value) => {
        setEditedEdu(prev => ({ ...prev, [field]: value }));
    };

    return (
        <div className={`bg-white rounded-2xl p-6 border-2 border-dashed ${isEditing ? 'border-blue-400 shadow-lg' : 'border-blue-100 hover:border-blue-200'} mb-6 relative group transition-all duration-300`}>
            {/* Header Actions */}
            <div className="flex items-start justify-between mb-6">
                {edu.fromCV ? <GeneratedBadge /> : <div />}
                <div className="flex items-center gap-2">
                    {isEditing ? (
                        <>
                            <button onClick={handleSave} className="flex items-center gap-2 px-3 h-8 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition-colors shadow-md">
                                <Check size={14} />
                                Sauvegarder
                            </button>
                            <button onClick={handleCancel} className="flex items-center gap-2 px-3 h-8 bg-white border border-slate-200 text-slate-500 rounded-lg text-xs font-bold hover:bg-slate-50 transition-colors">
                                <X size={14} />
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={onRemove}
                                className="w-8 h-8 flex items-center justify-center bg-white border border-slate-200 text-slate-400 rounded-lg hover:text-red-500 hover:border-red-200 transition-colors"
                                title="Supprimer"
                            >
                                <X size={16} />
                            </button>
                            <button
                                onClick={() => setIsEditing(true)}
                                className="flex items-center gap-2 px-3 h-8 bg-white border border-slate-200 text-slate-600 rounded-lg text-xs font-bold hover:border-blue-200 hover:text-blue-600 transition-colors"
                            >
                                <Pencil size={14} />
                                Modifier
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-4 md:grid md:grid-cols-[140px_1fr] md:gap-y-4 md:gap-x-4 md:items-baseline text-sm">
                <span className="text-slate-400 font-medium">Diplôme</span>
                {isEditing ? (
                    <input
                        type="text"
                        value={editedEdu.degree}
                        onChange={(e) => handleChange('degree', e.target.value)}
                        className="w-full p-2 border border-slate-200 rounded-lg font-bold text-slate-900 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                ) : (
                    <h4 className="font-black text-slate-900 text-base uppercase tracking-wide">{editedEdu.degree}</h4>
                )}

                <span className="text-slate-400 font-medium">École</span>
                {isEditing ? (
                    <input
                        type="text"
                        value={editedEdu.school}
                        onChange={(e) => handleChange('school', e.target.value)}
                        className="w-full p-2 border border-slate-200 rounded-lg font-bold text-slate-800 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                ) : (
                    <div className="font-bold text-slate-800">{editedEdu.school}</div>
                )}

                <span className="text-slate-400 font-medium">Date</span>
                {isEditing ? (
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={editedEdu.startDate}
                            placeholder="Début"
                            onChange={(e) => handleChange('startDate', e.target.value)}
                            className="w-32 p-2 border border-slate-200 rounded-lg text-slate-600 focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                        <span className="text-slate-400 font-bold">à</span>
                        <input
                            type="text"
                            value={editedEdu.endDate}
                            placeholder="Fin"
                            onChange={(e) => handleChange('endDate', e.target.value)}
                            className="w-32 p-2 border border-slate-200 rounded-lg text-slate-600 focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                    </div>
                ) : (
                    <span className="text-slate-600 font-medium">{editedEdu.startDate} - {editedEdu.endDate}</span>
                )}

                {(editedEdu.description || isEditing) && (
                    <>
                        <span className="text-slate-400 font-medium self-start mt-1">Description</span>
                        {isEditing ? (
                            <textarea
                                value={editedEdu.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                rows={3}
                                className="w-full p-2 border border-slate-200 rounded-lg text-slate-600 leading-relaxed focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                            />
                        ) : (
                            <p className="text-slate-600 leading-relaxed">{editedEdu.description}</p>
                        )}
                    </>
                )}

                {/* Compétences (Labels) */}
                <span className="text-slate-400 font-medium self-start mt-1">Compétences</span>
                <div className="w-full">
                    {isEditing ? (
                        <div className="space-y-3">
                            <div className="flex flex-wrap gap-2">
                                {(editedEdu.skills || []).map((skill, idx) => (
                                    <span key={idx} className="bg-blue-50 text-blue-700 px-3 py-1.5 rounded-xl text-xs font-bold border border-blue-100 flex items-center gap-2">
                                        {skill}
                                        <button onClick={() => handleRemoveSkill(skill)} className="text-blue-400 hover:text-red-500">
                                            <X size={12} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                            <div className="flex gap-2 max-w-xs">
                                <input
                                    type="text"
                                    value={newSkill}
                                    onChange={(e) => setNewSkill(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleAddSkill()}
                                    placeholder="Ajouter compétence..."
                                    className="flex-1 p-2 border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                    onClick={handleAddSkill}
                                    className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    <Plus size={14} />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {(editedEdu.skills || []).map((skill, idx) => (
                                <span key={idx} className="bg-blue-100 text-blue-700 px-2 py-1 rounded-md text-xs font-bold border border-blue-200">
                                    {skill}
                                </span>
                            ))}
                            {(!editedEdu.skills || editedEdu.skills.length === 0) && (
                                <span className="text-slate-400 italic text-xs">Aucune compétence renseignée</span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ProfilePage = () => {
    const [isCVModalOpen, setIsCVModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('info');
    const location = useLocation();
    const { user, signOut } = useAuth();
    const navigate = useNavigate();

    // Ouvre la modale d'upload CV à la demande (ex: fin de la visite guidée)
    useEffect(() => {
        const handler = () => setIsCVModalOpen(true);
        window.addEventListener('open-cv-upload-modal', handler);
        return () => window.removeEventListener('open-cv-upload-modal', handler);
    }, []);

    // Initial Profile State
    const [profile, setProfile] = useState({
        fullName: 'Chargement...',
        age: '',
        avatarUrl: null,
        email: '',
        joinedDate: '',
        experiences: [],
        formations: [],
        langues: [],
        skills: [],
        specializations: [],
        preferredCountries: [],
        isPremium: false,
        cvMatchCount: 0,
        lastMatchUpdate: null,
        cvUploadCount: 0,
        lastCvUpload: null,
        lastCVDataUpdate: null
    });

    const handleSignOut = async () => {
        await signOut();
        navigate('/login');
    };

    // 1. Fetch User Data on Mount
    useEffect(() => {
        const fetchProfile = async () => {
            if (!user) return;

            try {
                const { data, error } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', user.id)
                    .single();

                if (error) throw error;

                if (data) {
                    // Get CV Data if exists
                    const cvData = data.cv_data || {};

                    setProfile({
                        fullName: `${data.first_name || ''} ${data.last_name || ''}`.trim() || 'Utilisateur',
                        age: data.age || (data.age_range ? data.age_range : ''),
                        avatarUrl: data.avatar_url,
                        email: user.email || '',
                        joinedDate: data.created_at ? `Inscrit depuis le ${new Date(data.created_at).toLocaleDateString()}` : '',
                        experiences: (cvData.experiences || []).map(e => ({ ...e, validated: false })),
                        formations: (cvData.formations || []).map(f => ({ ...f, validated: false })),
                        langues: cvData.languages || [],
                        skills: cvData.skills || [],
                        // Use root preferred_sectors if available, fallback to cv_data
                        specializations: (data.preferred_sectors && data.preferred_sectors.length > 0) ? data.preferred_sectors : (cvData.specializations || []),
                        preferredCountries: data.preferred_countries || [],
                        isPremium: data.is_premium || false,
                        cvMatchCount: data.cv_match_update_count || 0,
                        lastMatchUpdate: data.last_match_update,
                        cvUploadCount: data.cv_upload_count || 0,
                        lastCvUpload: data.last_cv_update,
                        lastCVDataUpdate: data.last_cv_data_update
                    });
                }
            } catch (err) {
                console.error("Error fetching profile:", err);
            }
        };

        fetchProfile();
    }, [user]);

    // 2. Handle N8N CV Data Redirect
    useEffect(() => {
        if (location.state?.cvData && user?.id) {
            const data = location.state.cvData;
            console.log("📥 Received CV Data:", data);

            const saveData = async () => {
                try {
                    // cv_data est remplacé en bloc ci-dessous : on doit explicitement
                    // reporter ai_generation_history (compteur de crédits IA), sinon un
                    // ré-upload de CV réinitialiserait silencieusement les 3 essais gratuits.
                    const { data: existingProfile } = await supabase
                        .from('profiles')
                        .select('cv_data')
                        .eq('id', user.id)
                        .single();

                    const { error } = await supabase
                        .from('profiles')
                        .update({
                            cv_data: {
                                ...data,
                                ai_generation_history: existingProfile?.cv_data?.ai_generation_history || [],
                            },
                            ...(data.embedding ? { embedding: data.embedding } : {})
                        })
                        .eq('id', user.id);

                    if (error) console.error("Error saving CV data:", error);
                    else {
                        console.log("✅ CV Data & Embedding persisted to DB");
                        // Update local state to show new data immediately
                        setProfile(prev => ({
                            ...prev,
                            experiences: (data.experiences || []).map(e => ({ ...e, validated: false })),
                            formations: (data.formations || []).map(f => ({ ...f, validated: false })),
                            langues: data.languages || [],
                            skills: data.skills || [],
                            specializations: data.specializations || [],
                        }));
                    }
                } catch (err) {
                    console.error("Failed to save CV data:", err);
                }
            };
            saveData();
        }
    }, [location.state, user]);



    const handleSectionUpdate = (section, data) => {
        // 1. Optimistic Update
        setProfile(prev => ({ ...prev, [section]: data }));

        // 2. Persist to Supabase
        const saveToDb = async () => {
            try {
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) return;

                // SPECIAL HANDLING for Sectors and Countries (Write to ROOT columns)
                if (section === 'specializations' || section === 'preferredCountries') {
                    const updatePayload = {};
                    if (section === 'specializations') updatePayload.preferred_sectors = data; // Write to preferred_sectors
                    if (section === 'preferredCountries') updatePayload.preferred_countries = data;

                    // Also update CV data for consistency if it's specializations
                    // But we prioritize the root column logic for Onboarding persistence.

                    const { error } = await supabase
                        .from('profiles')
                        .update(updatePayload)
                        .eq('id', user.id);

                    if (error) throw error;
                    // We also want to update cv_data for specializations to keep it in sync for the parser logic?
                    // Let's do both for specializations.
                }

                // Map state keys to DB cv_data keys
                const keyMap = {
                    'experiences': 'experiences',
                    'formations': 'formations',
                    'langues': 'languages',
                    'skills': 'skills',
                    'specializations': 'specializations' // This goes to cv_data too
                };

                // If it's preferredCountries, we don't save to cv_data (it's not part of CV parsing usually)
                if (section === 'preferredCountries') return;

                const dbKey = keyMap[section] || section;

                // Fetch current data ...
                const { data: currentProfile } = await supabase
                    .from('profiles')
                    .select('cv_data')
                    .eq('id', user.id)
                    .single();

                const currentCvData = currentProfile?.cv_data || {};

                const updatedCvData = {
                    ...currentCvData,
                    [dbKey]: data
                };

                const { error } = await supabase
                    .from('profiles')
                    .update({
                        cv_data: updatedCvData,
                        last_cv_data_update: new Date().toISOString()
                    })
                    .eq('id', user.id);

                if (error) throw error;
                console.log(`✅ Saved ${section} to Supabase`);

            } catch (err) {
                console.error(`Error saving ${section}:`, err);
            }
        };

        saveToDb();
    };

    const handleAddExperience = () => {
        const newExp = {
            title: "",
            company: "",
            location: "",
            contractType: "",
            startDate: "",
            endDate: "",
            description: "",
            sector: [],
            fromCV: false,
            isNew: true
        };
        handleSectionUpdate('experiences', [newExp, ...profile.experiences]);
    };

    const handleAddFormation = () => {
        const newEdu = {
            degree: "",
            school: "",
            startDate: "",
            endDate: "",
            details: "",
            skills: [],
            fromCV: false,
            isNew: true
        };
        handleSectionUpdate('formations', [newEdu, ...profile.formations]);
    };



    const SidebarItem = ({ icon: Icon, label, id, subtitle, to }) => (
        <button
            onClick={() => to ? navigate(to) : setActiveTab(id)}
            className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${activeTab === id
                ? 'bg-white shadow-md border border-slate-100'
                : 'hover:bg-slate-100/50 text-slate-500'
                }`}
        >
            <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${activeTab === id ? 'bg-blue-600/10 text-blue-600' : 'bg-slate-50'}`}>
                    <Icon size={20} />
                </div>
                <div className="text-left">
                    <p className={`font-bold text-sm ${activeTab === id ? 'text-slate-900' : 'text-slate-500'}`}>{label}</p>
                    {subtitle && <p className="text-[10px] text-slate-400 font-medium">{subtitle}</p>}
                </div>
            </div>
            <ChevronRight size={18} className={activeTab === id ? 'text-blue-600' : 'text-slate-300'} />
        </button>
    );

    const DataField = ({ label, value, icon: Icon, readOnly = false, onSave, type = "text" }) => {
        const [isEditing, setIsEditing] = useState(false);
        const [editedValue, setEditedValue] = useState(value);

        useEffect(() => { setEditedValue(value); }, [value]);

        const handleSave = () => {
            onSave(editedValue);
            setIsEditing(false);
        };

        return (
            <div className="group py-6 border-b border-slate-100 last:border-0">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{label}</p>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 w-full mr-4">
                        {Icon && <Icon size={18} className="text-slate-400" />}
                        {isEditing ? (
                            <input
                                type={type}
                                value={editedValue}
                                onChange={(e) => setEditedValue(e.target.value)}
                                className="font-bold text-lg text-slate-800 border-b-2 border-blue-500 outline-none w-full bg-transparent"
                                autoFocus
                            />
                        ) : (
                            <p className={`font-bold text-lg ${readOnly ? 'text-slate-500 opacity-70' : 'text-slate-800'}`}>
                                {label === 'Âge' && value ? `${value} ans` : (value || 'Non renseigné')}
                            </p>
                        )}
                    </div>
                    {!readOnly && (
                        isEditing ? (
                            <div className="flex gap-2">
                                <button onClick={handleSave} className="p-2 bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100 transition-colors">
                                    <Check size={18} />
                                </button>
                                <button onClick={() => { setIsEditing(false); setEditedValue(value); }} className="p-2 text-slate-400 hover:bg-slate-50 rounded-full transition-colors">
                                    <X size={18} />
                                </button>
                            </div>
                        ) : (
                            <button onClick={() => setIsEditing(true)} className="p-2 hover:bg-slate-50 rounded-full transition-colors text-slate-300 hover:text-blue-600">
                                <Pencil size={18} />
                            </button>
                        )
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-[#F0F9FF] text-slate-900 font-inter">
            <Helmet><meta name='robots' content='noindex, nofollow' /></Helmet>


            <main className="max-w-7xl mx-auto px-6 py-10 flex flex-col lg:flex-row gap-10">
                {/* Left Sidebar */}
                <aside className="lg:w-1/3 space-y-8">
                    {/* User Mini Profile */}
                    <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-slate-100 shadow-sm">
                            <img
                                src={getAvatarFaceUrl(profile.avatarUrl)}
                                alt="Profile"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div>
                            <h2 className="font-bold text-lg text-slate-900">{profile.fullName}</h2>
                            <p className="text-xs text-slate-400 font-medium">{profile.joinedDate}</p>
                        </div>
                    </div>

                    {/* Navigation Menu */}
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <p className="px-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Mon compte</p>
                            <SidebarItem icon={User} label="Informations personnelles" id="info" />
                            <SidebarItem icon={Heart} label="Mes favoris" id="favorites" to="/mes-favoris" />
                        </div>

                        <div className="space-y-2">
                            <p className="px-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Sécurité & aides</p>
                            <SidebarItem icon={ShieldCheck} label="Confidentialité & sécurité" id="security" />
                            <SidebarItem icon={CreditCard} label="Abonnement Trouve+" id="premium" />
                            <button
                                onClick={() => window.dispatchEvent(new Event('restart-onboarding-tour'))}
                                className="w-full flex items-center justify-between p-4 rounded-xl transition-all hover:bg-slate-100/50 text-slate-500"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-slate-50">
                                        <Compass size={20} />
                                    </div>
                                    <p className="font-bold text-sm text-slate-500">Revoir la visite guidée</p>
                                </div>
                                <ChevronRight size={18} className="text-slate-300" />
                            </button>
                        </div>

                        <div className="pt-4 border-t border-slate-100">
                            <button
                                onClick={handleSignOut}
                                className="w-full flex items-center gap-3 p-4 rounded-xl text-slate-500 hover:bg-red-50 hover:text-red-600 transition-all"
                            >
                                <div className="p-2 rounded-lg bg-slate-50 group-hover:bg-red-100">
                                    <LogOut size={20} />
                                </div>
                                <span className="font-bold text-sm">Déconnexion</span>
                            </button>
                        </div>
                    </div>

                    {/* Trouve+ Card */}
                    <div className="bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] rounded-3xl p-6 text-white relative overflow-hidden shadow-2xl shadow-blue-900/20 group cursor-pointer hover:scale-[1.02] transition-all">
                        <div className="relative z-10">
                            <div className="flex items-center gap-2 mb-4">
                                <span className="text-xl font-black italic">Trouve<span className="text-blue-300">+</span></span>
                            </div>
                            <p className="text-sm font-medium text-blue-100 opacity-90 leading-relaxed mb-6">
                                Les meilleures chances de trouver votre VIE à l'international !
                            </p>
                            <Link to="/trouve-plus" className="block w-full bg-white text-blue-900 py-3 rounded-xl font-bold text-sm shadow-xl hover:bg-blue-50 transition-colors text-center">
                                Découvrir Trouve+
                            </Link>
                        </div>
                        {/* Decorative circles */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl group-hover:bg-white/20 transition-colors" />
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-400/20 rounded-full translate-y-1/2 -translate-x-1/2 blur-xl" />
                    </div>
                </aside>

                {/* Mobile Action Buttons - Between Trouve+ and Informations personnelles */}
                <div className="flex flex-col gap-3 md:hidden">
                    <button
                        onClick={() => setIsCVModalOpen(true)}
                        className="flex items-center justify-center gap-3 bg-blue-600 text-white px-6 py-3.5 rounded-2xl font-black text-sm shadow-xl shadow-blue-600/30 active:scale-95 transition-all w-full"
                    >
                        <Sparkles size={18} />
                        Mettre à jour mon profil
                    </button>
                </div>

                {/* Right Content */}
                <section className="flex-1 space-y-8">
                    <div className="bg-white rounded-[32px] md:rounded-[40px] p-5 md:p-10 shadow-sm border border-slate-100">
                        <div className="flex items-center justify-between mb-10">
                            <h1 className="text-3xl font-black text-slate-900 tracking-tight">Informations personnelles</h1>
                            <div className="hidden md:flex gap-4">

                                <button
                                    onClick={() => setIsCVModalOpen(true)}
                                    className="flex items-center gap-3 bg-blue-600 text-white px-6 py-3 rounded-2xl font-black text-sm shadow-xl shadow-blue-600/30 hover:scale-105 active:scale-95 transition-all hover:bg-blue-700"
                                >
                                    <Sparkles size={18} />
                                    Mettre à jour mon profil
                                </button>
                            </div>
                        </div>

                        {/* Profile Picture Mockup */}
                        <div className="bg-slate-50 rounded-3xl p-8 flex flex-col md:flex-row items-center gap-8 mb-12 border border-slate-100">
                            <div className="relative group">
                                <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-md">
                                    <img
                                        src={getAvatarFaceUrl(profile.avatarUrl)}
                                        alt="Profile"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <button className="absolute bottom-0 right-0 w-8 h-8 bg-slate-900 text-white rounded-full flex items-center justify-center border-4 border-white shadow-lg hover:scale-110 transition-transform">
                                    <Camera size={14} />
                                </button>
                            </div>
                            <p className="text-slate-500 text-sm font-medium leading-relaxed max-w-sm">
                                Le profil doit être rempli de manière exhaustive pour permettre d'afficher les offres les plus pertinentes, notamment avec CV matching.
                            </p>
                        </div>

                        {/* Data Sections */}
                        <div className="space-y-2">
                            <DataField label="Prénom et Nom" value={profile.fullName} readOnly />
                            <DataField
                                label="Âge"
                                value={profile.age}
                                icon={Calendar}
                                type="number"
                                onSave={async (newAge) => {
                                    setProfile(prev => ({ ...prev, age: newAge }));
                                    await supabase.from('profiles').update({ age: parseInt(newAge) }).eq('id', (await supabase.auth.getUser()).data.user.id);
                                }}
                            />

                            <div className="mt-12 pt-12 border-t border-slate-100">
                                <h3 className="text-xl font-black text-slate-900 mb-6">Coordonnées</h3>
                                <DataField label="E-mail" value={profile.email} icon={Mail} readOnly />
                            </div>

                            {/* EXPERIENCE SECTION */}
                            <div className="mt-12 pt-12 border-t border-slate-100">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-xl font-black text-slate-900">Expérience Professionnelle</h3>
                                    <button
                                        onClick={handleAddExperience}
                                        className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-xl text-xs font-bold hover:bg-blue-600 hover:text-white transition-all shadow-sm border border-blue-100"
                                    >
                                        <Plus size={14} />
                                        Ajouter
                                    </button>
                                </div>
                                {profile.experiences.length > 0 ? (
                                    <div className="bg-slate-50 rounded-3xl p-6 border border-slate-100">
                                        {profile.experiences.map((exp, idx) => (
                                            <ExperienceCard
                                                key={idx}
                                                exp={exp}
                                                isNew={exp.isNew}
                                                onUpdate={(updatedExp) => {
                                                    const newExps = [...profile.experiences];
                                                    // Mark as validated on update/save, remove isNew flag
                                                    const { isNew, ...rest } = updatedExp;
                                                    newExps[idx] = { ...rest, validated: true };
                                                    handleSectionUpdate('experiences', newExps);
                                                }}
                                                onValidate={() => {
                                                    const newExps = [...profile.experiences];
                                                    newExps[idx].validated = true;
                                                    handleSectionUpdate('experiences', newExps);
                                                }}
                                                onRemove={() => {
                                                    const newExps = [...profile.experiences];
                                                    newExps.splice(idx, 1);
                                                    handleSectionUpdate('experiences', newExps);
                                                }}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-slate-50/50 rounded-3xl p-12 border border-dashed border-slate-200 text-center">
                                        <p className="text-sm text-slate-400 italic">Aucune expérience renseignée.</p>
                                    </div>
                                )}
                            </div>

                            {/* EDUCATION SECTION */}
                            <div className="mt-12 pt-12 border-t border-slate-100">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-xl font-black text-slate-900">Formation</h3>
                                    <button
                                        onClick={handleAddFormation}
                                        className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-xl text-xs font-bold hover:bg-blue-600 hover:text-white transition-all shadow-sm border border-blue-100"
                                    >
                                        <Plus size={14} />
                                        Ajouter
                                    </button>
                                </div>
                                {profile.formations.length > 0 ? (
                                    <div className="bg-slate-50 rounded-3xl p-6 border border-slate-100">
                                        {profile.formations.map((edu, idx) => (
                                            <FormationCard
                                                key={idx}
                                                edu={edu}
                                                isNew={edu.isNew}
                                                onUpdate={(updatedEdu) => {
                                                    const newForms = [...profile.formations];
                                                    const { isNew, ...rest } = updatedEdu;
                                                    newForms[idx] = { ...rest, validated: true };
                                                    handleSectionUpdate('formations', newForms);
                                                }}
                                                onValidate={() => {
                                                    const newForms = [...profile.formations];
                                                    newForms[idx].validated = true;
                                                    handleSectionUpdate('formations', newForms);
                                                }}
                                                onRemove={() => {
                                                    const newForms = [...profile.formations];
                                                    newForms.splice(idx, 1);
                                                    handleSectionUpdate('formations', newForms);
                                                }}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-slate-50/50 rounded-3xl p-12 border border-dashed border-slate-200 text-center">
                                        <p className="text-sm text-slate-400 italic">Aucune formation renseignée.</p>
                                    </div>
                                )}
                            </div>

                            {/* SPECIALIZATIONS SECTION */}
                            <SpecializationSection
                                specs={profile.specializations}
                                onUpdate={(newSpecs) => handleSectionUpdate('specializations', newSpecs)}
                            />

                            {/* COUNTRIES SECTION */}
                            <CountriesSection
                                countries={profile.preferredCountries}
                                onUpdate={(newCountries) => handleSectionUpdate('preferredCountries', newCountries)}
                            />

                            {/* SKILLS & LANGUAGES SECTION */}
                            <div className="mt-12 pt-12 border-t border-slate-100">
                                <h3 className="text-xl font-black text-slate-900 mb-6">Compétences & Langues</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Languages */}
                                    <LanguagesCard
                                        languages={profile.langues}
                                        onUpdate={(newLangs) => handleSectionUpdate('langues', newLangs)}
                                    />

                                    {/* Skills */}
                                    <SkillsCard
                                        skills={profile.skills}
                                        onUpdate={(newSkills) => handleSectionUpdate('skills', newSkills)}
                                    />
                                </div>
                            </div>



                        </div>
                    </div>

                    {/* Footer Info */}
                    <p className="text-center text-slate-400 text-[11px] px-8 leading-relaxed">
                        TrouvetonVIE s'engage pour la confidentialité de vos données. Toutes vos informations sont protégées conformément au RGPD et analysées de manière anonyme par l'IA Mistral.
                    </p>
                </section>
            </main>

            <CVSelectionModal
                isOpen={isCVModalOpen}
                onClose={() => setIsCVModalOpen(false)}
                uploadCount={profile.cvUploadCount}
                lastUpload={profile.lastCvUpload}
            />
        </div >
    );
};

export default ProfilePage;
